import { isArray, isNotUndefined } from '../typeguards.js';

/**
 * Ensures its input is an array.
 * If the input is not already an array, it wraps the input in an array.
 *
 * @param input a value which is an array or not
 * @returns an array
 */
export const ensureArray = <T>(input: T | T[]) => isArray(input) ? input : [input];

/**
 * Creates an array of elements split into two groups, the first of which contains elements predicate returns truthy for,
 * while the second of which contains elements predicate returns falsey for.
 *
 * @example const [pass, fail] = partition(myArray, (e) => e > 5);
 */
export const partition = <T>(elements: T[], condition: (e: T) => boolean): [T[], T[]] => {
    return elements.reduce<[T[], T[]]>(([pass, fail], element) => {
        return condition(element) ? [[...pass, element], fail] : [pass, [...fail, element]];
    }, [[], []]);
};

/**
 * Filter duplicates from an array of elements.
 * This function is called for each element of an array.
 *
 * @example
 *  const result = [1,2,3,2,1].filter(filterDuplicates);
 *    -> [1,2,3]
 *
 * @param value a value to deduplicate
 * @param index the index of the given value in the array
 * @param array the whole original array
 * @returns an array without duplicates
 */
export const filterDuplicates = <T>(value: T, index: number, array: T[]) => array.indexOf(value) === index;

/**
 * A range of numbers in the form: [min, max]
 *
 * e.g. range [5,9] represents the numbers 5,6,7,8,9
 */
export type Range = [number, number];

/**
 * Generates number ranges for the given steps.
 *
 * @example
 * toRanges([1,10,20,30]) -> [[1,9],[10,19],[20,29]]
 *
 * @param steps the min points of the ranges
 * @returns an array of ranges
 */
export const toRanges = (steps: number[]): Range[] => steps.map<Range | undefined>((value, index, arr) => isNotUndefined(arr[index + 1]) ? [value, (arr[index + 1] ?? 1e4) - 1] : undefined).filter(isNotUndefined);

/**
 * Selects a range by a given value.
 *
 * @example
 * selectRange(15)([10,19]);
 *   -> true
 * selectRange(15)([1,9]);
 *   -> false
 * toRanges([1,10,20,30]).find(selectRange(15));
 *   -> [10,19]
 *
 * @param value value to compare with ranges
 * @returns true if the range matches
 */
export const selectRange = (value: number) => ([min, max]: Range): boolean => (min <= value && value <= max);
